import React, { useEffect, useState } from 'react';
import { Modal, Button, Table, Select, Form, message } from 'antd';
import { useQuery } from '@apollo/client';
import ImageUploader from '../../../components/ImageUploader';
import { SiteType } from '../../../graphql/types/SiteType';
import { hasHeadquartersAccess, isFinancialTeam, isKAQC, isCSFollowUp } from '../../../utils/auth';
import { useTranslation } from 'react-i18next';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { useGetAllAccessHubAndWarehousesForRole } from '../../../hooks/useWarehouseQuery';
import { OrderType } from '../../../graphql/types/OrderType';
import { CloseOrderInput } from '../graphql/mutations/closeOrder';
import { t } from '../../../i18n';
import {
    ExceptionDictionaryResponse,
    GET_ALL_EXCEPTION
} from '../../delivery/parcels/graphql/queries/getAllException';
import { useStatusInternationalization } from '../../delivery/parcels/hooks/useStatusInternationalization';

// 显示模式枚举
enum DisplayMode {
  SHOW_HUB_WAREHOUSE = 'show-hub-warehouse',
  HIDE_HUB_WAREHOUSE = 'hide-hub-warehouse'
}

interface ParcelRow {
  orderId: number;
  trackingNumber: string;
}

export interface BatchCloseOrderInput {
  orderId: number;
  closeType: string;
  exceptionReasonCode?: string;
  exceptionPriority?: number;
  imageS3Keys?: string[];
  exceptionHubId?: string;
  exceptionWarehouseId?: string | null;
}

interface BatchCloseOrderModalProps {
  visible: boolean;
  parcels: ParcelRow[];
  onOk: (orders: BatchCloseOrderInput[]) => void;
  onCancel: () => void;
  loading?: boolean;
}

interface FormValues {
  closeType: string | null;
  exceptionReasonCode: string | null;
  exceptionHubId: string | null;
  exceptionWarehouseId: string | null;
  sharedImageS3Keys?: string[]; // RETURNED类型共用照片
  parcels: {
    orderId: number;
    imageS3Keys: string[];
  }[];
}

const BatchCloseOrderModal: React.FC<BatchCloseOrderModalProps> = ({
  visible,
  parcels,
  onOk,
  onCancel,
  loading = false
}) => {
  const [form] = Form.useForm<FormValues>();
  const [warehouseListForHubData, setWarehouseListForHubData] = useState<any[]>([]);
  const [closeType, setCloseType] = useState<string>('');
  
  // 查询异常原因字典数据
  const { data: exceptionData } = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION);
  const exceptionList = exceptionData?.getExceptionDictionaryForHeadquarters || [];
  const { getExceptionReasonText } = useStatusInternationalization();
  const { i18n } = useTranslation();
  
  const isHeadquartersStaffRole = hasHeadquartersAccess();
  const isKAQCRole = isKAQC();
  const isFinancialTeamRole = isFinancialTeam();
  const isCSFollowUpRole = isCSFollowUp();

  // 获取当前仓库信息
  const { currentWarehouse } = useWarehouseContext();

  // 判断显示模式
  const getDisplayMode = (): DisplayMode => {
    if ((isHeadquartersStaffRole || isKAQCRole || isFinancialTeamRole || isCSFollowUpRole) && !currentWarehouse) {
      return DisplayMode.SHOW_HUB_WAREHOUSE;
    }
    return DisplayMode.HIDE_HUB_WAREHOUSE;
  };

  const displayMode = getDisplayMode();

  // 数据查询 - 统一使用 useGetAllAccessHubAndWarehousesForRole
  const {
    warehouses: warehouseListData,
    loading: warehousesLoading,
    error: warehousesError
  } = useGetAllAccessHubAndWarehousesForRole({
    skip: displayMode === DisplayMode.HIDE_HUB_WAREHOUSE
  });

  const warehouseListDataFiltered = warehouseListData.filter(warehouse => warehouse.siteType === SiteType.WAREHOUSE);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setCloseType('');
      setWarehouseListForHubData([]);
      const initialValues: FormValues = {
        closeType: null,
        exceptionReasonCode: null,
        exceptionHubId: null,
        exceptionWarehouseId: null,
        sharedImageS3Keys: [],
        parcels: parcels.map(row => ({
          orderId: row.orderId,
          imageS3Keys: []
        }))
      };
      form.setFieldsValue(initialValues);
    }
  }, [visible, form, parcels]);

  // 监听表单 Hub 字段变化，自动设置对应的仓库列表
  useEffect(() => {
    if (visible && warehouseListData && displayMode === DisplayMode.SHOW_HUB_WAREHOUSE) {
      const hubId = form.getFieldValue('exceptionHubId');
      if (hubId) {
        const filteredWarehouses = warehouseListDataFiltered.filter(warehouse => warehouse.parentSiteId === hubId);
        setWarehouseListForHubData(filteredWarehouses);
      }
    }
  }, [visible, warehouseListData, form, displayMode, warehouseListDataFiltered]);

  // 当数据加载完成后，更新 warehouse 列表
  useEffect(() => {
    if (visible && warehouseListData && warehouseListData.length > 0) {
      if (displayMode === DisplayMode.SHOW_HUB_WAREHOUSE) {
        setWarehouseListForHubData([]);
      }
    }
  }, [visible, warehouseListData, displayMode, currentWarehouse]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const { closeType, exceptionReasonCode, exceptionHubId, exceptionWarehouseId, sharedImageS3Keys, parcels: parcelValues } = values;

      // 检查必需字段
      if (!closeType) {
        message.error(t('common.required'));
        return;
      }

      // 如果是 LOST 或 DELIVERY_FAILED，需要异常原因
      if ((closeType === 'LOST' || closeType === 'DELIVERY_FAILED') && !exceptionReasonCode) {
        message.error(t('common.required'));
        return;
      }

      // 如果是 LOST、RETURN_SCAN 或 RETURNED 类型，需要中心（Hub）
      if ((closeType === 'LOST' || closeType === 'RETURN_SCAN' || closeType === 'RETURNED') 
          && displayMode !== DisplayMode.HIDE_HUB_WAREHOUSE && !exceptionHubId) {
        message.error(t('common.required'));
        return;
      }

      // 注意：图片不是必填项，后端定义中 imageS3Keys 是可选的
      // 已移除图片必填检查逻辑

      // 构造提交数据
      const submitData: BatchCloseOrderInput[] = parcelValues.map(row => ({
        orderId: row.orderId,
        closeType: closeType!,
        exceptionReasonCode: (closeType === 'LOST' || closeType === 'DELIVERY_FAILED') ? (exceptionReasonCode || undefined) : undefined,
        exceptionPriority: 100,
        // RETURNED类型使用共用照片，其他类型使用各自照片
        imageS3Keys: closeType === 'RETURNED' 
          ? sharedImageS3Keys 
          : (closeType !== 'LOST' ? row.imageS3Keys : undefined),
        // 所有类型都支持Hub和Warehouse
        exceptionHubId: exceptionHubId ? exceptionHubId : undefined,
        exceptionWarehouseId: exceptionWarehouseId ? exceptionWarehouseId : null
      }));

      onOk(submitData);
    } catch (error) {
      console.log('Please complete all required fields.');
      message.error(t('common.required'));
    }
  };

  const handleCloseTypeChange = (value: string) => {
    setCloseType(value);
    // 重置异常原因和包裹的图片
    const currentHubId = form.getFieldValue('exceptionHubId');
    const currentWarehouseId = form.getFieldValue('exceptionWarehouseId');
    
    form.setFieldsValue({
      exceptionReasonCode: undefined,
      sharedImageS3Keys: [],
      parcels: parcels.map(row => ({
        orderId: row.orderId,
        imageS3Keys: []
      })),
      // 切换类型时不清空Hub和Warehouse，保持用户选择
      exceptionHubId: currentHubId,
      exceptionWarehouseId: currentWarehouseId
    });
    // 清除中心字段的验证错误，让新的验证规则生效
    if (value !== 'LOST' && value !== 'RETURN_SCAN' && value !== 'RETURNED') {
      form.setFields([
        { name: 'exceptionHubId', errors: [] }
      ]);
    }
  };

  const handleReasonChange = (value: string) => {
    // 只重置包裹的图片，保持其他字段不变
    const currentHubId = form.getFieldValue('exceptionHubId');
    const currentWarehouseId = form.getFieldValue('exceptionWarehouseId');
    
    form.setFieldsValue({
      parcels: parcels.map(row => ({
        orderId: row.orderId,
        imageS3Keys: []
      })),
      exceptionHubId: currentHubId,
      exceptionWarehouseId: currentWarehouseId
    });
  };

  // Hub 切换处理函数
  const handleHubChange = (value: string) => {
    const filteredWarehouses = warehouseListDataFiltered?.filter(warehouse => warehouse.parentSiteId === value) || [];
    setWarehouseListForHubData(filteredWarehouses);
    form.setFieldsValue({
      exceptionWarehouseId: undefined
    });
  };

  /**
   * 获取关单操作中异常原因的显示文本
   * 与 MarkAsException 保持一致，统一使用后端的 secondaryTypeEn
   */
  const getCloseOrderExceptionReasonText = (exception: any, exceptionReasonCode: string): string => {
    // 统一使用通用的显示方式，与 MarkAsException 保持一致
    return getExceptionReasonText(exception, exceptionReasonCode);
  };

  /**
   * 将后端返回的错误消息转换为多语言消息
   */
  const getLocalizedErrorMessage = (errorMessage: string | null | undefined): string => {
    if (!errorMessage) {
      return t('orderClose.closeFailed');
    }

    // 错误消息到翻译键的映射
    const errorMessageMap: Record<string, string> = {
      'Delivery failed can only be applied to orders in DELIVERY_IN_PROGRESS status': 'orderClose.validationErrors.deliveryFailedInvalidStatus',
      'Return scan can only be applied to orders with RETURN next action': 'orderClose.validationErrors.returnScanInvalidNextAction',
      'Return scan requires return warehouse scan completed': 'orderClose.validationErrors.returnScanRequiresWarehouseScan',
      'Returned can only be applied to orders with RETURN_SCAN event record': 'orderClose.validationErrors.returnedRequiresReturnScanEvent',
      'Exception reason code is required for LOST close type': 'orderClose.validationErrors.lostRequiresExceptionReason',
      'Order has already been closed. Cannot close the same order twice.': 'orderClose.validationErrors.alreadyClosed',
    };

    // 处理包含动态参数的错误消息（如订单ID）
    if (errorMessage.startsWith('Order not found with id: ')) {
      const orderId = errorMessage.replace('Order not found with id: ', '');
      return t('orderClose.validationErrors.orderNotFound', { orderId });
    }

    // 查找对应的翻译键
    const translationKey = errorMessageMap[errorMessage];
    if (translationKey) {
      const translated = t(translationKey);
      // 如果翻译键不存在，返回原始消息
      return translated !== translationKey ? translated : errorMessage;
    }

    // 如果没有找到对应的翻译键，返回原始消息
    return errorMessage;
  };

  const getExceptionReasonOptions = () => {
    // 根据关单类型过滤异常原因
    const allowedCodes = closeType === 'LOST' 
      ? ['ERHUB003', 'ERHUB008', 'ERHUB005']
      : closeType === 'DELIVERY_FAILED'
      ? ['ERDL002']
      : [];
    
    // 从异常原因字典中查找对应的异常原因，并使用专门的显示函数格式化显示
    return exceptionList
      .filter(ex => allowedCodes.includes(ex.exceptionReasonCode))
      .map(ex => ({
        value: ex.exceptionReasonCode,
        label: getCloseOrderExceptionReasonText(ex, ex.exceptionReasonCode)
      }));
  };

  const getColumns = () => {
    const baseColumns = [
      {
        title: t('components.modal.trackingNumber'),
        dataIndex: 'orderId',
        align: 'center' as const,
        width: 260,
        render: (_: any, record: any, idx: number) => parcels[idx]?.trackingNumber || record.orderId
      },
      {
        title: t('orderClose.fields.closeType'),
        dataIndex: 'closeType',
        align: 'center' as const,
        width: 200,
        render: () => {
          const type = form.getFieldValue('closeType');
          if (!type) return '';
          switch (type) {
            case 'LOST':
              return t('orderClose.closeType.LOST');
            case 'DELIVERY_FAILED':
              return t('orderClose.closeType.DELIVERY_FAILED');
            case 'RETURN_SCAN':
              return t('orderClose.closeType.RETURN_SCAN');
            case 'RETURNED':
              return t('orderClose.closeType.RETURNED');
            default:
              return type;
          }
        }
      }
    ];

    // 如果是 LOST 或 DELIVERY_FAILED，添加异常原因列
    if (closeType === 'LOST' || closeType === 'DELIVERY_FAILED') {
      baseColumns.push({
        title: t('orderClose.fields.exceptionReason'),
        dataIndex: 'exceptionReasonCode',
        align: 'center' as const,
        width: 200,
        render: () => {
          const reasonCode = form.getFieldValue('exceptionReasonCode');
          const options = getExceptionReasonOptions();
          const option = options.find(opt => opt.value === reasonCode);
          return option ? option.label : '';
        }
      } as any);
    }

    // 如果不是 LOST 和 RETURNED，添加照片列（每个订单独立上传）
    // 注意：图片不是必填项，后端定义中 imageS3Keys 是可选的
    if (closeType && closeType !== 'LOST' && closeType !== 'RETURNED') {
      baseColumns.push({
        title: t('orderClose.fields.uploadPhotos'),
        dataIndex: 'imageS3Keys',
        align: 'left' as const,
        width: 260,
        render: (_: any, __: any, idx: number) => {
          return (
            <Form.Item
              name={['parcels', idx, 'imageS3Keys']}
            >
              <ImageUploader maxCount={2} />
            </Form.Item>
          );
        }
      } as any);
    } else if (closeType === 'LOST') {
      baseColumns.push({
        title: t('orderClose.fields.uploadPhotos'),
        dataIndex: 'imageS3Keys',
        align: 'left' as const,
        width: 260,
        render: () => {
          return <span className="text-gray-500">{t('orderClose.noPhotoRequired')}</span>;
        }
      } as any);
    } else if (closeType === 'RETURNED') {
      // RETURNED类型显示共用照片提示
      baseColumns.push({
        title: t('orderClose.fields.uploadPhotos'),
        dataIndex: 'imageS3Keys',
        align: 'left' as const,
        width: 260,
        render: () => {
          return <span className="text-gray-500">{t('orderClose.useSharedPhoto')}</span>;
        }
      } as any);
    }

    return baseColumns;
  };

  const columns = getColumns();

  return (
    <Modal
      title={<div className="flex items-center"><div className="w-1 h-6 bg-indigo-600 mr-2"></div><span>{t('orderClose.operateClose')} {parcels.length} {t('orderClose.parcels')}</span></div>}
      open={visible}
      onCancel={onCancel}
      width={900}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>{t('components.button.cancel')}</Button>,
        <Button key="ok" type="primary" onClick={handleOk} loading={loading}>{t('components.button.ok')}</Button>
      ]}
      maskClosable={!loading}
      closable={!loading}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={t('orderClose.fields.closeType') + ':'}
          name="closeType"
          rules={[{ required: true, message: t('common.required') || '' }]}
        >
          <Select
            style={{ width: 260 }}
            onChange={handleCloseTypeChange}
            placeholder={t('common.pleaseSelect')}
          >
            <Select.Option value="LOST">{t('orderClose.closeType.LOST')}</Select.Option>
            <Select.Option value="DELIVERY_FAILED">{t('orderClose.closeType.DELIVERY_FAILED')}</Select.Option>
            <Select.Option value="RETURN_SCAN">{t('orderClose.closeType.RETURN_SCAN')}</Select.Option>
            <Select.Option value="RETURNED">{t('orderClose.closeType.RETURNED')}</Select.Option>
          </Select>
        </Form.Item>

        {(closeType === 'LOST' || closeType === 'DELIVERY_FAILED') && (
          <Form.Item
            label={t('orderClose.fields.exceptionReason') + ':'}
            name="exceptionReasonCode"
            rules={[{ required: true, message: t('common.required') || '' }]}
          >
            <Select
              style={{ width: 260 }}
              onChange={handleReasonChange}
              placeholder={t('common.pleaseSelect')}
            >
              {getExceptionReasonOptions().map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {/* RETURNED类型显示共用照片上传 */}
        {/* 注意：图片不是必填项，后端定义中 imageS3Keys 是可选的 */}
        {closeType === 'RETURNED' && (
          <Form.Item
            label={t('orderClose.fields.uploadPhotos') + ':'}
            name="sharedImageS3Keys"
          >
            <ImageUploader maxCount={2} />
          </Form.Item>
        )}

        {/* 根据显示模式决定是否显示Hub和Warehouse选择器，所有类型都支持 */}
        {displayMode !== DisplayMode.HIDE_HUB_WAREHOUSE && (
          <>
            <Form.Item
              label={t('components.modal.hub')}
              name="exceptionHubId"
              rules={(closeType === 'LOST' || closeType === 'RETURN_SCAN' || closeType === 'RETURNED') ? [{ required: true, message: t('common.required') }] : []}
            >
              <Select
                style={{ width: 260 }}
                placeholder={t('components.modal.selectHub')}
                allowClear
                onChange={handleHubChange}
              >
                {warehouseListData?.filter(it => it.siteType === SiteType.HUB).map(hub => {
                  return (
                    <Select.Option key={hub.id} value={hub.id}>
                      {hub.name}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>

            <Form.Item
              label={t('components.modal.warehouse')}
              name="exceptionWarehouseId"
            >
              <Select
                style={{ width: 260 }}
                placeholder={t('components.modal.selectWarehouse')}
                allowClear
              >
                {form.getFieldValue('exceptionHubId') && warehouseListForHubData.length > 0 && (
                  <>
                    {warehouseListForHubData.map(warehouse => (
                      <Select.Option
                        key={warehouse.id}
                        value={warehouse.id}
                      >
                        {warehouse.name}
                      </Select.Option>
                    ))}
                  </>
                )}
              </Select>
            </Form.Item>
          </>
        )}

        <Form.Item name="parcels" noStyle>
          <Table
            dataSource={parcels}
            columns={columns}
            rowKey={(_, idx) => (idx !== undefined ? idx.toString() : '')}
            pagination={false}
            size="small"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default BatchCloseOrderModal;
